import { useEffect, useRef } from "react";
import { AppBridge, PostMessageTransport } from "@modelcontextprotocol/ext-apps/app-bridge";
import type { CallToolRequest, CallToolResult } from "@modelcontextprotocol/sdk/types.js";
import { apiMutate } from "~/utils/apiClient";

const { VITE_ANALYSIS_AGENT_API, VITE_CUSTOMER } = import.meta.env;

const HOST_INFO = { name: "manifold-nextgen", version: "1.0.0" };

interface McpAppPaneFrameProps {
  // Exactly one is set — `html` for legacy inline `ui://` bundles, `url` for resources
  // migrated to serve a URL (see `fetchMcpAppResource`).
  html: string | null;
  url: string | null;
  title: string;
  serverId: string;
  // Latest render state; pushed into the iframe on init and on every change.
  state: unknown;
  // Called with the fresh state after a panel-initiated tool call, so the pane
  // can keep the tab (title, dedupe key) in sync with edits made in the panel.
  onLocalUpdate: (state: unknown) => void;
}

function currentThemeMode(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/**
 * Wraps a panel payload as an MCP `CallToolResult`. `structuredContent` is the field a spec
 * panel actually reads, but it is specified as an object, so a non-object payload travels only
 * as the JSON text fallback rather than being dropped or coerced.
 */
function toCallToolResult(content: unknown): CallToolResult {
  const isObject = typeof content === "object" && content !== null && !Array.isArray(content);
  return {
    content: [{ type: "text", text: JSON.stringify(content) }],
    ...(isObject ? { structuredContent: content as Record<string, unknown> } : {}),
  };
}

/**
 * A pane-hosted MCP-app iframe that pushes state updates in place and routes
 * panel-initiated edits back to the server. Speaks the `ui/initialize` →
 * `ui/tool_result` postMessage contract as `McpAppFrame`, re-posting whenever
 * `state` changes so a single iframe reflects the agent's running edits rather
 * than remounting per tool call. Additionally, a `tools/call` message from the
 * iframe (the app-to-server direction) is proxied to the analysis-agent, and
 * the returned state is pushed straight back into the iframe.
 *
 * Panels may speak either of two protocols, and the frame answers whichever one the panel
 * opens with. The bare `{type: ...}` messages above predate the MCP Apps spec and are what the
 * deployed Cohort Explorer panel uses; an `AppBridge` alongside them serves panels built on
 * `@modelcontextprotocol/ext-apps`, whose JSON-RPC framing the bare handler ignores and vice
 * versa. New panels should use the SDK - see `apps/demo-mcp` for the reference implementation.
 */
export function McpAppPaneFrame({
  html,
  url,
  title,
  serverId,
  state,
  onLocalUpdate,
}: McpAppPaneFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  const bridgeRef = useRef<AppBridge | null>(null);
  const specReadyRef = useRef(false);
  // Read inside the message handler without re-subscribing on every change.
  const stateRef = useRef(state);
  stateRef.current = state;
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;
  const onLocalUpdateRef = useRef(onLocalUpdate);
  onLocalUpdateRef.current = onLocalUpdate;
  // Identity of the iframe's loaded document — a change reloads the iframe, so effects that
  // need to re-initialize against the new document key off this instead of `url` alone.
  const frameKey = url ?? html;

  // A new url/html reloads the iframe, so it must re-initialize.
  useEffect(() => {
    readyRef.current = false;
  }, [frameKey]);

  useEffect(() => {
    function postResult(content: unknown, id?: unknown) {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "ui/tool_result", id, result: { content } },
        "*"
      );
    }

    // Nextgen's dark-mode toggle sets this class on <html> (see
    // ManifoldNextGenLayout) — read it directly rather than threading a prop
    // through, since it's DOM chrome state, not app state.
    function postTheme() {
      const mode = document.documentElement.classList.contains("dark") ? "dark" : "light";
      iframeRef.current?.contentWindow?.postMessage(
        { type: "ui/theme", customer: VITE_CUSTOMER, mode },
        "*"
      );
    }

    function handleMessage(event: MessageEvent) {
      // The sandbox attribute below has no allow-same-origin, so this iframe's origin is
      // opaque ("null") regardless of srcDoc vs src — event.origin can't be allowlisted, so
      // checking event.source against this iframe's contentWindow is the equivalent check.
      // Same reason postMessage below targets "*" rather than a specific origin: there is no
      // origin to target.
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data as
        | {
            type?: string;
            id?: unknown;
            name?: string;
            arguments?: Record<string, unknown>;
          }
        | undefined;

      if (data?.type === "ui/initialize") {
        readyRef.current = true;
        postResult(stateRef.current);
        postTheme();
        return;
      }

      if (data?.type === "tools/call" && typeof data.name === "string") {
        const callId = data.id;
        const name = data.name;
        apiMutate<unknown>(`${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`, "POST", {
          server_id: serverIdRef.current,
          tool: name,
          arguments: data.arguments ?? {},
        })
          .then((result) => {
            onLocalUpdateRef.current(result);
            postResult(result, callId);
          })
          .catch((error: unknown) => {
            iframe.contentWindow?.postMessage(
              {
                type: "ui/tool_result",
                id: callId,
                error: { message: String(error) },
              },
              "*"
            );
          });
      }
    }
    window.addEventListener("message", handleMessage);

    // Forward live dark-mode toggles to an already-initialized iframe.
    const observer = new MutationObserver(() => {
      if (readyRef.current) postTheme();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      window.removeEventListener("message", handleMessage);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (readyRef.current && iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ type: "ui/tool_result", result: { content: state } }, "*");
    }
  }, [state]);

  // The MCP Apps side of the same two jobs the effect above does: answer the panel's
  // handshake, then keep it fed. Rebuilt per `frameKey` because a new url/html reloads the frame.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      return;
    }
    const bridge = new AppBridge(null, HOST_INFO, { serverTools: {} });

    bridge.oncalltool = async (params: CallToolRequest["params"]) => {
      const result = await apiMutate<unknown>(
        `${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`,
        "POST",
        {
          server_id: serverIdRef.current,
          tool: params.name,
          arguments: params.arguments ?? {},
        }
      );
      onLocalUpdateRef.current(result);
      return toCallToolResult(result);
    };

    // Marks the panel as spec-speaking, which is what routes later state pushes here rather
    // than to the bare-message path. `sendToolInput` is required before any `sendToolResult`.
    bridge.addEventListener("initialized", () => {
      specReadyRef.current = true;
      void bridge.sendToolInput({ arguments: {} });
      void bridge.sendToolResult(toCallToolResult(stateRef.current));
      void bridge.sendHostContextChange({ theme: currentThemeMode(), customer: VITE_CUSTOMER });
    });

    bridgeRef.current = bridge;
    void bridge.connect(new PostMessageTransport(iframe.contentWindow, iframe.contentWindow));

    const observer = new MutationObserver(() => {
      if (specReadyRef.current) {
        void bridge.sendHostContextChange({ theme: currentThemeMode(), customer: VITE_CUSTOMER });
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      observer.disconnect();
      specReadyRef.current = false;
      bridgeRef.current = null;
      void bridge.close();
    };
  }, [frameKey]);

  useEffect(() => {
    if (specReadyRef.current) {
      void bridgeRef.current?.sendToolResult(toCallToolResult(state));
    }
  }, [state]);

  return (
    <iframe
      ref={iframeRef}
      {...(url !== null ? { src: url } : { srcDoc: html ?? undefined })}
      sandbox="allow-scripts allow-top-navigation-by-user-activation"
      title={title}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
      }}
    />
  );
}
